import {
  cloneElement,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import useOnClickOutside from 'project/hooks/useOnClickOutside'

export type DropdownType = {
  children: ReactNode
  className?: string
  dropdownClassName?: string
  dropdownContent: ReactNode | ReactNode[]
  hidden?: boolean
  position?: 'right'
  rightPosition?: number
  setShow: (show: boolean) => void
  show: boolean
} & React.HTMLAttributes<HTMLDivElement>

const Dropdown: FC<DropdownType> = ({
  children,
  className,
  dropdownClassName,
  dropdownContent: _dropdownContent,
  hidden,
  position,
  rightPosition,
  setShow,
  show,
  ...props
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  const dropdownContent = useMemo(
    () =>
      Array.isArray(_dropdownContent)
        ? _dropdownContent.flat(Infinity).filter(Boolean)
        : _dropdownContent,
    [_dropdownContent],
  )

  const closeDropdown = useCallback(() => {
    setShow(false)
    setHighlightedIndex(null)
  }, [setShow])

  const handleClickOutside = useCallback(() => {
    if (show) {
      closeDropdown()
    }
  }, [show, closeDropdown])

  useOnClickOutside(ref, handleClickOutside, 'mousedown')

  const handleItemClick = useCallback(
    (itemOnClick?: () => void) => {
      itemOnClick?.()
      closeDropdown()
    },
    [closeDropdown],
  )

  // Keyboard navigation
  useEffect(() => {
    if (
      !show ||
      !Array.isArray(dropdownContent) ||
      dropdownContent.length === 0
    ) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const itemCount = dropdownContent.length

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setHighlightedIndex((prev) =>
            prev === null || prev === itemCount - 1 ? 0 : prev + 1,
          )
          break
        case 'ArrowUp':
          event.preventDefault()
          setHighlightedIndex((prev) =>
            prev === null || prev === 0 ? itemCount - 1 : prev - 1,
          )
          break
        case 'Enter':
          if (highlightedIndex !== null) {
            const item = dropdownContent[highlightedIndex] as ReactElement
            handleItemClick(item?.props?.onClick)
          }
          break
        case 'Escape':
          closeDropdown()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [show, dropdownContent, highlightedIndex, handleItemClick, closeDropdown])

  // Keep highlighted item in view
  useEffect(() => {
    if (!ref.current || highlightedIndex === null) return

    const container = ref.current.querySelector('.dropdown-menu') as HTMLElement
    const items = ref.current.querySelectorAll('.dropdown-item')
    const item = items[highlightedIndex] as HTMLElement

    if (!container || !item) return

    const containerRect = container.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()

    if (itemRect.bottom > containerRect.bottom) {
      container.scrollTop =
        item.offsetTop + item.offsetHeight - container.offsetHeight
    } else if (itemRect.top < containerRect.top) {
      container.scrollTop = item.offsetTop
    }
  }, [highlightedIndex])

  const renderDropdownItems = () => {
    if (!Array.isArray(dropdownContent)) {
      return dropdownContent
    }

    return dropdownContent.map((item, index) => {
      const element = item as ReactElement
      return cloneElement(element, {
        className: classNames(element.props?.className, {
          active: index === highlightedIndex,
        }),
        key: index,
        onClick: () => handleItemClick(element.props?.onClick),
      })
    })
  }

  return (
    <div
      ref={ref}
      className={classNames('dropdown position-relative', className)}
      aria-expanded={show}
      {...props}
    >
      {children}
      {!hidden && (
        <div
          style={{ minWidth: '100%', right: rightPosition }}
          className={show ? 'py-1 position-absolute' : ''}
        >
          <div
            className={classNames(
              'dropdown-menu p-0',
              { 'right-0': position === 'right', show },
              dropdownClassName,
            )}
          >
            {renderDropdownItems()}
          </div>
        </div>
      )}
    </div>
  )
}

Dropdown.displayName = 'Dropdown'
export default Dropdown
